package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.ComplaintsSnapshot
import br.com.vvrefeicoes.portal.domain.model.CountItem
import br.com.vvrefeicoes.portal.domain.model.CustomerComplaint
import br.com.vvrefeicoes.portal.domain.model.MonthCount
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.unidadeKeyOf
import br.com.vvrefeicoes.portal.domain.repository.ComplaintsDashboardRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class ComplaintsDashboardController(
    private val repository: ComplaintsDashboardRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var selection by mutableStateOf(PeriodSelection.current(dateTimeProvider.currentPeriod()))
        private set

    var snapshot by mutableStateOf(ComplaintsSnapshot.Empty)
        private set

    private var started = false
    private var unitFilter: CompanyRef? = null
    private var managerFilter: String? = null
    private var directorFilter: String? = null
    private var authorizedUnits: List<CompanyRef> = emptyList()
    private var orgChartData: OrgChartData? = null

    val records: List<CustomerComplaint>
        get() = snapshot.records

    val selectedRecords: List<CustomerComplaint>
        get() = snapshot.records

    val totalOccurrences: Int
        get() = snapshot.total

    val evolution: List<MonthCount>
        get() = snapshot.evolution

    val byShift: List<CountItem>
        get() = snapshot.byShift

    val byService: List<CountItem>
        get() = snapshot.byService

    val byNature: List<CountItem>
        get() = snapshot.byNature

    val byUnit: List<CountItem>
        get() = snapshot.byUnit

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyPeriod(newSelection: PeriodSelection) {
        if (newSelection == selection) return
        selection = newSelection
        scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyFilter(
        unit: CompanyRef?,
        manager: String?,
        director: String?,
        authorizedUnits: List<CompanyRef>,
    ) {
        if (unit == unitFilter && manager == managerFilter && director == directorFilter && authorizedUnits == this.authorizedUnits) return
        unitFilter = unit
        managerFilter = manager
        directorFilter = director
        this.authorizedUnits = authorizedUnits
        scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val data =
            orgChartData ?: orgChartRepository
                .loadInitial()
                .currentVersion.data
                .also { orgChartData = it }
        val unitId = unitFilter?.let { data.unidadeKeyOf(it) }
        snapshot = repository.load(selection, unitId, managerFilter, directorFilter)
    }
}

@Composable
fun rememberComplaintsDashboardController(): ComplaintsDashboardController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        ComplaintsDashboardController(
            repository = appGraph.complaintsDashboardRepository,
            orgChartRepository = appGraph.orgChartRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
